import { Component, createRef, memo } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import axios from "axios";

import ReactPlayer from "react-player";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FieldSubmitForm from "./FieldSubmitForm";
import FieldCanvas from "./FieldCanvas";
import Preloader from "@/components/Elements/Preloader";

import { FieldMarkingHintsConstant, FieldPoints, Stages } from "./constants";
import { markupApi } from "@/api";

import withRouter from "@/components/Elements/WithRouter";
import "../../scss/components/_field.scss";

const saveCalibrationToState = (camera) => (camera !== null ? camera : "");
const saveCalibrationToSubmit = (camera) => (camera !== "" ? +camera : null);

class Field extends Component {
  constructor(props) {
    super(props);

    this.state = {
      firstStageEditor: false,
      secondStageEditor: false,
      hint: "",
      field: {
        width: 0,
        height: 0,
        points: [],
        zone: [],
        updated: null,
      },
      updated: false,
      cameraModelList: null,
      cameraModelSelected: "",
      frame: null,
      showFrame: false,
      videoSource: null,
      frameError: null,
      viewerScale: 1,
      viewerX: 0,
      viewerY: 0,
    };

    this.ref = createRef();
    this.canvas = createRef();
    this.reactPlayerRef = createRef();
    this.viewerRef = createRef();

    this.handleModeChange = this.handleModeChange.bind(this);
    this.handleSelectedPointChange = this.handleSelectedPointChange.bind(this);
    this.handleSelectedCamera = this.handleSelectedCamera.bind(this);
    this.secondStageButtonIsDisabled =
      this.secondStageButtonIsDisabled.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onUpdate = this.onUpdate.bind(this);
    this.handleShowFrame = this.handleShowFrame.bind(this);
    this.handleViewerZoom = this.handleViewerZoom.bind(this);
    this.handleViewerMouseDown = this.handleViewerMouseDown.bind(this);
    this.handleViewerMouseMove = this.handleViewerMouseMove.bind(this);
    this.handleViewerMouseUp = this.handleViewerMouseUp.bind(this);
    this.handleViewerWheel = this.handleViewerWheel.bind(this);
    this._isPanning = false;
    this._panStart = { x: 0, y: 0 };
  }

  componentDidMount() {
    axios
      .all([
        markupApi.getFieldMeta(this.props.match.params.videoId),
        markupApi.getCalibrationModels(),
      ])
      .then(
        axios.spread((fieldMeta, cameraModelList) => {
          const { field, source, calibration } = fieldMeta;
          this.setState((state) => ({
            ...state,
            field,
            // cameraModelList: cameraModelList.data,
            cameraModelSelected: saveCalibrationToState(calibration),
            videoSource: source,
          }));
        })
      );
  }

  componentDidUpdate() {
    if (!this.state.updated) {
      this.ref?.current?.onValuesUpdate({
        width: this.state.field?.width,
        height: this.state.field?.height,
        penaltyAreaLength: this.state.field?.penalty_area_length ? String(this.state.field.penalty_area_length) : '',
        penaltyAreaWidth: this.state.field?.penalty_area_width ? String(this.state.field.penalty_area_width) : '',
        goalAreaLength: this.state.field?.goal_area_length ? String(this.state.field.goal_area_length) : '',
        goalAreaWidth: this.state.field?.goal_area_width ? String(this.state.field.goal_area_width) : '',
        goalWidth: this.state.field?.goal_width ? String(this.state.field.goal_width) : '',
      });
      this.canvas?.current?.resetPoints(this.state.field?.points);
      this.canvas?.current?.resetZonePoints(this.state.field?.zone_points);
      this.setState((state) => ({
        ...state,
        updated: true,
        hint: FieldMarkingHintsConstant.DEFAULT,
      }));
    }
  }

  handlePointsRemove() {
    if (this.canvas.current) {
      this.canvas.current.resetPoints(null);
    }
  }

  // Handle: editor mode for the iframe and canvas
  handleModeChange(stage) {
    this.setState((state) => ({
      ...state,
      [stage]: !state[stage],
    }));
    this.setState((state) => ({
      ...state,
      hint: state.firstStageEditor
        ? FieldMarkingHintsConstant.FIRST_STAGE
        : state.secondStageEditor
        ? FieldMarkingHintsConstant.SECOND_STAGE
        : FieldMarkingHintsConstant.DEFAULT,
    }));
  }

  secondStageButtonIsDisabled() {
    return (
      this.state.firstStageEditor ||
      this.state.field.fieldPoints?.length < 4 ||
      !this.state.field.height ||
      !this.state.field.width
    );
  }

  handleViewerZoom(deltaY) {
    this.setState((state) => {
      const scaleBy = 1.15;
      const newScale = deltaY < 0 ? state.viewerScale * scaleBy : state.viewerScale / scaleBy;
      return { ...state, viewerScale: Math.max(1, Math.min(8, newScale)) };
    });
  }

  handleViewerMouseDown(e) {
    // Pan: middle-click, or Shift+click, or right-click when zoomed
    const canPan = e.button === 1 || e.shiftKey || (e.button === 2 && this.state.viewerScale > 1);
    if (canPan) {
      e.preventDefault();
      this._isPanning = true;
      this._panStart = { x: e.clientX - this.state.viewerX, y: e.clientY - this.state.viewerY };
    }
  }

  handleViewerMouseMove(e) {
    if (!this._isPanning) return;
    e.preventDefault();
    this.setState((state) => ({
      ...state,
      viewerX: e.clientX - this._panStart.x,
      viewerY: e.clientY - this._panStart.y,
    }));
  }

  handleViewerMouseUp() {
    this._isPanning = false;
  }

  handleViewerWheel(e) {
    if (this.state.viewerScale <= 1 && e.deltaY > 0) return; // don't zoom below 1
    e.preventDefault();
    e.stopPropagation();
    this.handleViewerZoom(e.deltaY);
  }

  onChange(values) {
    this.setState((state) => ({
      ...state,
      field: {
        ...state.field,
        height: values.height,
        width: values.width,
        penaltyAreaLength: values.penaltyAreaLength,
        penaltyAreaWidth: values.penaltyAreaWidth,
        goalAreaLength: values.goalAreaLength,
        goalAreaWidth: values.goalAreaWidth,
        goalWidth: values.goalWidth,
        zoneEnabled: values.zoneEnabled || state.field.zoneEnabled,
      },
    }));
  }

  canSubmit() {
    const { width, height } = this.state.field;
    return width && height;
  }

  onSubmit = async () => {
    let points = this.canvas.current.state.points.map((point) => ({
      id: point.id,
      x: point.x,
      y: point.y,
    }));
    if (![4, 6, 9].includes(points.length)) {
      alert("The number of points on the field must be one of 9, 6 or 4!");
      return;
    }

    // Collect zone points (only those that were moved from defaults)
    const zonePoints = (this.canvas.current.state.zonePoints || []).map((p) => ({
      id: p.id,
      x: p.x,
      y: p.y,
    }));

    const toInt = (v) => {
      const n = parseInt(v, 10);
      return isNaN(n) || n <= 0 ? null : n;
    };

    const ze = this.state.field.zoneEnabled || {};

    const body = {
      width: this.state.field.width,
      height: this.state.field.height,
      points,
      zone_points: zonePoints,
      penalty_area_length: ze.penalty ? toInt(this.state.field.penaltyAreaLength) : null,
      penalty_area_width: ze.penalty ? toInt(this.state.field.penaltyAreaWidth) : null,
      goal_area_length: ze.goalArea ? toInt(this.state.field.goalAreaLength) : null,
      goal_area_width: ze.goalArea ? toInt(this.state.field.goalAreaWidth) : null,
      goal_width: ze.goalposts ? toInt(this.state.field.goalWidth) : null,
      calibration: saveCalibrationToSubmit(this.state.cameraModelSelected),
    };

    try {
      await markupApi.updateFieldMeta(this.props.match.params.videoId, body);
      alert("The form is filled out correctly. Results saved");
    } catch (error) {
      alert(`The form has been filled out incorrectly. Error result: ${error}`);
    }
  };

  onUpdate() {
    this.forceUpdate();
  }

  handleSelectedPointChange(e) {
    if (this.canvas.current) {
      this.canvas.current.changeActive(e.target.value);
    }
  }

  handleSelectedCamera(e) {
    const currentCameraModel = e.target.value;

    this.setState((state) => ({
      ...state,
      cameraModelSelected: currentCameraModel,
      frameError: null,
    }));
  }

  handleShowFrame() {
    const currentTime = (
      this.reactPlayerRef?.current?.getCurrentTime() * 100
    ).toFixed(0);
    this.setState((state) => ({
      ...state,
      showFrame: !state.showFrame,
    }));
    if (this.state.showFrame) {
      this.setState((state) => ({
        ...state,
        pendingFrame: false,
      }));
    } else {
      const { videoId } = this.props.match.params;
      const { cameraModelSelected } = this.state;
      markupApi
        .getCalibrationFrame(videoId, cameraModelSelected, currentTime)
        .then((res) => {
          const data = Buffer.from(res.data, "binary").toString("base64");
          const frame = `data:${res.headers[
            "content-type"
          ].toLowerCase()};base64,${data}`;
          this.setState((state) => ({
            ...state,
            frame,
            pendingFrame: false,
            frameError: null,
          }));
        })
        .catch(() => {
          this.setState((state) => ({
            ...state,
            frameError: "Loading error",
            pendingFrame: false,
            showFrame: false,
          }));
        });
      this.setState((state) => ({
        ...state,
        pendingFrame: true,
      }));
    }
  }

  render() {
    const points =
      this.canvas.current &&
      this.state.firstStageEditor &&
      this.canvas.current.state.points.map((point) => (
        <option key={point.id} value={point.id}>
          {point.desc} [{point.x.toFixed(2)} : {point.y.toFixed(2)}]
        </option>
      ));

    return (
      <>
        {this.state.videoSource ? (
          <Container className="p-3" fluid>
            <Row>
              <Col>
                <div
                  className="viewer-wrapper"
                  style={{
                    overflow: "hidden",
                    cursor: this._isPanning ? "grabbing" : "default",
                  }}
                  onWheel={this.handleViewerWheel}
                  onMouseDown={this.handleViewerMouseDown}
                  onMouseMove={this.handleViewerMouseMove}
                  onMouseUp={this.handleViewerMouseUp}
                  onMouseLeave={this.handleViewerMouseUp}
                  onContextMenu={(e) => this.state.viewerScale > 1 && e.preventDefault()}
                  onDoubleClick={() => this.setState({ viewerScale: 1, viewerX: 0, viewerY: 0 })}
                >
                  <div
                    className="viewer ratio ratio-16x9"
                    ref={this.viewerRef}
                    style={{
                      transform: `translate(${this.state.viewerX}px, ${this.state.viewerY}px) scale(${this.state.viewerScale})`,
                      transformOrigin: "center center",
                    }}
                  >
                    <ReactPlayer
                      ref={this.reactPlayerRef}
                      width="100%"
                      height="100%"
                      controls={true}
                      url={this.state.videoSource}
                    />
                    {this.state.showFrame && !this.state.pendingFrame && (
                      <img id="frame" src={this.state.frame} alt="frame" />
                    )}
                    <FieldCanvas
                      ref={this.canvas}
                      state={this.state}
                      onUpdate={this.onUpdate}
                      onZoom={this.handleViewerZoom}
                    />
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className="d-flex flex-column">
                <Button
                  variant="outline-dark"
                  className="viewer__button my-2"
                  disabled={this.state.secondStageEditor}
                  onClick={() => this.handleModeChange(Stages.FIRST)}
                >
                  Field marking:{" "}
                  {this.state.firstStageEditor
                    ? "Редактирование ✏️"
                    : "Просмотр 🔍"}
                </Button>
                <FieldSubmitForm
                  ref={this.ref}
                  width={this.state.width}
                  height={this.state.height}
                  isZoneStage={this.state.secondStageEditor}
                  onChange={this.onChange}
                  onSubmit={this.onSubmit}
                />
                <Button
                  variant="outline-danger"
                  className="viewer__button"
                  onClick={() => this.handlePointsRemove(FieldPoints.FIELD)}
                >
                  Clear
                </Button>
              </Col>
              <Col className="d-flex flex-column">
                <Button
                  variant="outline-dark"
                  className="viewer__button my-2"
                  onClick={() => this.handleModeChange(Stages.SECOND)}
                  disabled={this.secondStageButtonIsDisabled()}
                >
                  Zone calibration:{" "}
                  {this.state.secondStageEditor
                    ? "Редактирование ✏️"
                    : "Просмотр 🔍"}
                </Button>
                <Button
                  variant="outline-danger"
                  className="viewer__button"
                  onClick={() => this.canvas?.current?.resetZonePoints(null)}
                  disabled={!this.state.secondStageEditor}
                >
                  Clear zones
                </Button>
              </Col>
              <Col>
                <Button
                  type="submit"
                  variant="outline-primary"
                  className="form__form_button confirm-data my-2 w-100"
                  disabled={!this.canSubmit()}
                  onClick={() => this.ref.current.submit()}
                >
                  Send 🚀
                </Button>
                <select
                  value={this.canvas.current?.state.active || -1}
                  className="form-select my-2 w-100"
                  onChange={this.handleSelectedPointChange}
                  disabled={!this.state.firstStageEditor}
                >
                  <option value={-1} disabled hidden>
                    Select a point on the field
                  </option>
                  {points}
                </select>
                <div className="flex-column">
                  <div className="d-flex">
                    <Button
                      variant={this.state.showFrame ? "secondary" : "primary"}
                      disabled={this.state.cameraModelSelected === ""}
                      onClick={this.handleShowFrame}
                    >
                      {this.state.pendingFrame ? (
                        <FontAwesomeIcon spin icon={["fas", "spinner"]} />
                      ) : (
                        <FontAwesomeIcon
                          icon={[
                            "fas",
                            this.state.showFrame ? "eye-slash" : "eye",
                          ]}
                        />
                      )}
                    </Button>
                    {/*<select
                                            name="select-camera-model"
                                            value={this.state.cameraModelSelected}
                                            className="form-select w-100"
                                            onChange={this.handleSelectedCamera}
                                        >
                                            <option value="" label="Выберете модель камеры"/>
                                            {cameraModelList?.map(
                                                ({
                                                     id,
                                                     name
                                                 }) => (
                                                    <option
                                                        key={id}
                                                        value={id}
                                                        label={name}
                                                    />
                                                )
                                            )}
                                        </select>*/}
                  </div>
                  {this.state.frameError && (
                    <div className="alert alert-warning" role="alert">
                      {this.state.frameError}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        ) : (
          <Preloader />
        )}
      </>
    );
  }
}

export default withRouter(memo(Field));
